import { AfterViewInit, Component, inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { ConfigService } from '../../shared-services/config.service';
import { CommonModule } from '@angular/common';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PaginatorComponent } from '../paginator/paginator.component';
import { PageEvent } from '@angular/material/paginator';
import { Subscription } from 'rxjs';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';

import {
  isFrozenColumn as _isFrozenColumn,
  isLastFrozenColumn as _isLastFrozenColumn,
  getFrozenColumnLeft as _getFrozenColumnLeft,
  isFrozenBodyRow as _isFrozenBodyRow,
  getFrozenBodyRowTop as _getFrozenBodyRowTop,
  getFrozenBodyRowBottom as _getFrozenBodyRowBottom,
} from '../../utils/table-freeze.utils';
import { formatDisplayText } from '../../utils/text-case.utils';

@Component({
  selector: 'app-table',
  imports: [MatTableModule, CommonModule, MatProgressSpinnerModule, PaginatorComponent],
  standalone: true,
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  displayedColumns: string[] = [];
  columnWidths: { [col: string]: number } = {};
  protected readonly DEFAULT_COL_WIDTH: number = 120;
  filter: any;
  isLoading = true;
  errorMessage: string | null = null;
  columnLabels: { [key: string]: string } = {};
  dataSource: any[] = [];
  private unSubscribe$: Subject<void> = new Subject();
  @Input() set widgetId(val: string) {
    this._widgetId = val;
  }
  get widgetId(): string {
    return this._widgetId || this.data?.widgetId;
  }
  private _widgetId!: string;
  pagedData: any[] = [];
  private originalDataSource: any[] = [];
  activeLegend: string | null = null;
  treeLayout: {
    topLeftLabel: string;
    topHeaderValue: string;
    groupRowLabel: string;
    itemRowLabel: string;
    subItemRowLabel: string;
    groupHeaders: { label: string; span: number }[];
    itemHeaders: string[];
    subItemHeaders: string[];
    metricRows: { label: string; values: any[] }[];
    leafMeta: any[];
    rootData: any;
    totalLeafColumns: number;
  } | null = null;

  private pageSize: number = 11;
  private pageIndex: number = 0;

  get treeRowCount(): number {
    if (!this.treeLayout) return 1;
    const baseRows = 4; // top row + group + item + sub-item
    const metricRows = this.treeLayout.metricRows?.length || 0;
    return Math.max(1, baseRows + metricRows);
  }

  // Column collapse/expand properties
  collapsedColumns = new Set<string>();
  private preCollapseWidths: { [col: string]: number } = {};
  protected readonly COLLAPSED_COL_WIDTH = 36;

  @ViewChild('paginatorComp') paginatorComp!: PaginatorComponent;
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private chartFilterHandler = inject(ChartFilterHandlerService);
  private reflowSub?: Subscription;
  private resolvedContainerHeight: string = '100%';

  // ─── Freeze Row / Column (delegated to shared utility) ─────────────────────

  isFrozenColumn(column: string): boolean {
    return _isFrozenColumn(this.data, this.displayedColumns, column);
  }

  isLastFrozenColumn(column: string): boolean {
    return _isLastFrozenColumn(this.data, this.displayedColumns, column);
  }

  getFrozenColumnLeft(column: string): number | null {
    return _getFrozenColumnLeft(this.data, this.displayedColumns, this.columnWidths, this.DEFAULT_COL_WIDTH, column);
  }

  isFrozenBodyRow(rowIndex: number): boolean {
    const rowCount = this.data?.isPagination ? this.pagedData.length : this.dataSource.length;
    return _isFrozenBodyRow(this.data, rowIndex, rowCount);
  }

  getFrozenBodyRowTop(rowIndex: number): number | null {
    const rowCount = this.data?.isPagination ? this.pagedData.length : this.dataSource.length;
    return _getFrozenBodyRowTop(this.data, rowIndex, rowCount);
  }

  getFrozenBodyRowBottom(rowIndex: number): number | null {
    const rowCount = this.data?.isPagination ? this.pagedData.length : this.dataSource.length;
    return _getFrozenBodyRowBottom(this.data, rowIndex, rowCount);
  }

  protected initColumnWidths(): void {
    const configured: { [key: string]: number } = this.data?.columnWidths || {};
    for (const col of this.displayedColumns || []) {
      if (this.columnWidths[col] === undefined) {
        this.columnWidths[col] = configured[col] ?? this.DEFAULT_COL_WIDTH;
      }
    }
  }

  constructor(
    private configService: ConfigService,
  ) {}

  ngAfterViewInit(): void {
    this.updateResolvedContainerHeight();

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.updateResolvedContainerHeight();
    });

    const initialFilters = this.wrapper.attach(
      this.data,
      {
        onReload: (filters) => this.reloadTable(filters),
        getChart: () => null,
        getSeriesNames: () => [],
        getCategories: () => [],
      },
      'category',
    );

    if (this.data?.callApiInDefault !== false) {
      this.reloadTable(initialFilters ?? undefined);
    }

    if (this.data?.callApiInDefault === false && initialFilters) {
      this.reloadTable(initialFilters);
    }
  }

  private reloadTable(filters?: any): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.loadAndRenderChart(filters)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => {
          const mappedTreeLayout = this.buildTreeTableLayout(res);
          if (mappedTreeLayout) {
            this.treeLayout = mappedTreeLayout;
            this.isLoading = false;
            return;
          }

          this.treeLayout = null;

          let tableData = res?.options ?? res?.data ?? (Array.isArray(res) ? res : []) ?? [];
          this.isLoading = false;
          const hasPriceFields = tableData.every(
            (row: any) => typeof row?.proposed_option_price === 'number' && typeof row?.current_option_price === 'number',
          );
          if (hasPriceFields) {
            tableData = tableData.map((row: any) => ({
              ...row,
              difference: Number((row.proposed_option_price - row.current_option_price).toFixed(3)),
            }));
            tableData.sort((a: any, b: any) => b.difference - a.difference);
          }
          this.originalDataSource = tableData;
          this.dataSource = [...this.originalDataSource];
          if (!!this.data?.isPagination) {
            this.setPagedData();
          }

          this.displayedColumns = this.data?.headerColumns?.map((col: any) => col.key) || [];
          const headerCase = (this.data?.textCase || 'capital') as any;
          this.columnLabels = Object.fromEntries(
            this.data?.headerColumns?.map((col: any) => [
              col.key,
              formatDisplayText(col.label || col.key, headerCase),
            ]) || [],
          );

          this.initColumnWidths();

          setTimeout(() => {
            this.paginatorComp?.updatePagination(this.dataSource.length, this.pageIndex);
          }, 200);
        },
        error: (err: any) => {
          this.isLoading = false;
          this.errorMessage = this.data?.noDataText || 'Failed to load table data. Please try again.';
          console.error('Error loading table data', err);
        },
      });
  }

  private updateResolvedContainerHeight(): void {
    const resolved = this.responsiveLayoutService.resolveWidgetHeight(this.data);
    const px = this.responsiveLayoutService.resolveHeightToPixels(resolved);
    this.resolvedContainerHeight = px ? `${px}px` : resolved || '100%';
  }

  get containerHeight(): string {
    return this.resolvedContainerHeight;
  }

  get treeRowHeightPx(): number | null {
    if (!this.treeLayout) return null;
    const containerPx = this.responsiveLayoutService.resolveHeightToPixels(this.resolvedContainerHeight);
    if (!containerPx) return null;
    const rows = 4 + (this.treeLayout.metricRows?.length || 0);
    if (rows <= 0) return null;
    return Math.max(34, Math.floor(containerPx / rows));
  }

  private buildTreeTableLayout(res: any): {
    topLeftLabel: string;
    topHeaderValue: string;
    groupRowLabel: string;
    itemRowLabel: string;
    subItemRowLabel: string;
    groupHeaders: { label: string; span: number }[];
    itemHeaders: string[];
    subItemHeaders: string[];
    metricRows: { label: string; values: any[] }[];
    leafMeta: any[];
    rootData: any;
    totalLeafColumns: number;
  } | null {
    const cfg = this.data?.treeTableConfig;
    if (!cfg?.enabled) return null;

    const rootPath = this.safeText(cfg.rootPath || '').trim();
    const root = rootPath ? this.getByPath(res, rootPath, null) : res;
    if (!root || typeof root !== 'object') return null;

    const groupPath = this.safeText(cfg.groupPath || '').trim();
    const groupLabelKey = this.safeText(cfg.groupLabelKey || '').trim();
    const itemPath = this.safeText(cfg.itemPath || '').trim();
    const itemLabelKey = this.safeText(cfg.itemLabelKey || '').trim();
    const itemSubLabelKey = this.safeText(cfg.itemSubLabelKey || '').trim();

    if (!groupPath || !groupLabelKey || !itemPath || !itemLabelKey || !itemSubLabelKey) {
      return null;
    }

    const groups: any[] = this.getByPath(root, groupPath, []);
    if (!Array.isArray(groups) || !groups.length) return null;

    const topLeftLabel = this.safeText(cfg.topLeftLabel ?? cfg.rowHeaderLabel ?? '');
    const configuredTopValuePath = (cfg.topValuePath || '').toString().trim();
    const topValueFromPath = configuredTopValuePath
      ? this.getByPath(root, configuredTopValuePath, null)
      : null;
    const topHeaderValue = this.safeText(
      topValueFromPath
      ?? cfg.topValue
      ?? '',
    );
    const groupRowLabel = this.safeText(cfg.groupRowLabel ?? '');
    const itemRowLabel = this.safeText(cfg.itemRowLabel ?? '');
    const subItemRowLabel = this.safeText(cfg.subItemRowLabel ?? '');

    const columnMeta: any[] = [];
    const groupHeaders: { label: string; span: number }[] = [];
    for (const group of groups) {
      const items = this.getByPath(group, itemPath, []);
      if (!Array.isArray(items) || !items.length) continue;

       const groupLabel = this.safeText(group?.[groupLabelKey]);
      groupHeaders.push({ label: groupLabel, span: items.length });

      for (const item of items) {
        columnMeta.push({
          group,
          item,
          groupLabel,
          itemLabel: this.safeText(item?.[itemLabelKey]),
          itemSubLabel: this.safeText(item?.[itemSubLabelKey]),
        });
      }
    }

    if (!columnMeta.length) return null;

    const metricRows = Array.isArray(cfg.metricRows) ? cfg.metricRows : [];
    const mappedMetricRows: { label: string; values: any[] }[] = [];
    for (const metric of metricRows) {
      const metricLabel = this.safeText(metric?.label || metric?.valuePath || metric?.valueKey || 'Value');
      const metricValuePath = metric?.valuePath || metric?.valueKey;
      const format = this.safeText(metric?.format || '');

      const values: any[] = [];
      columnMeta.forEach((meta: any) => {
        const raw = metricValuePath ? this.getByPath(meta.item, metricValuePath) : undefined;
        values.push(this.formatMetric(raw, format));
      });
      mappedMetricRows.push({ label: metricLabel, values });
    }

    return {
      topLeftLabel,
      topHeaderValue,
      groupRowLabel,
      itemRowLabel,
      subItemRowLabel,
      groupHeaders,
      itemHeaders: columnMeta.map((meta) => meta.itemLabel),
      subItemHeaders: columnMeta.map((meta) => meta.itemSubLabel),
      metricRows: mappedMetricRows,
      leafMeta: columnMeta,
      rootData: root,
      totalLeafColumns: columnMeta.length,
    };
  }

  onFlatTableCellClick(row: any): void {
    if (!this.data?.filterKey) return;
    this.chartFilterHandler.updateFilterSelection(this.data, row, row);
  }

  onTreeLeafClick(colIndex: number): void {
    if (!this.data?.filterKey || !this.treeLayout) return;

    const leaf = this.treeLayout.leafMeta?.[colIndex];
    if (!leaf) return;

    const payload = {
      ...(leaf.group || {}),
      ...(leaf.item || {}),
    };

    const cfg = this.data?.treeTableConfig || {};
    const clickContextMap = cfg?.clickContextMap;
    if (clickContextMap && typeof clickContextMap === 'object') {
      for (const [targetKey, sourcePath] of Object.entries(clickContextMap)) {
        const source = this.safeText(sourcePath || '').trim();
        if (!source) continue;
        const value = this.getByPath(this.treeLayout.rootData, source, undefined);
        if (value !== undefined) {
          (payload as any)[targetKey] = value;
        }
      }
    }

    this.chartFilterHandler.updateFilterSelection(this.data, payload, payload);
  }

  private getByPath(obj: any, path: string, fallback?: any): any {
    if (!path) return obj ?? fallback;
    const tokens = path.split('.').filter(Boolean);
    let current = obj;

    for (const token of tokens) {
      if (current === null || current === undefined) return fallback;
      if (Array.isArray(current) && /^\d+$/.test(token)) {
        current = current[Number(token)];
      } else {
        current = current[token as keyof typeof current];
      }
    }

    return current === undefined ? fallback : current;
  }

  private safeText(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value);
  }

  private formatMetric(value: any, format: string): any {
    if (value === null || value === undefined) return '';

    const n = Number(value);
    if (!Number.isFinite(n)) return value;

    if (format === 'compact-k') {
      const sign = n < 0 ? '-' : '';
      const abs = Math.abs(n);
      if (abs >= 1000) return `${sign}${Math.round(abs / 1000)}k`;
      return `${sign}${Math.round(abs)}`;
    }

    if (format === 'integer') {
      return Math.round(n);
    }

    return n;
  }

  private loadAndRenderChart(filters?: any): any {
    return this.configService
      .getConfig(this.data?.apiUrl, this.data, filters, this.data?.method || 'post', true)
      .pipe(takeUntil(this.unSubscribe$));
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event?.pageSize;
    this.pageIndex = event?.pageIndex;
    this.setPagedData();
  }

  private setPagedData() {
    const startIndex = (this.pageIndex || 0) * (this.pageSize || 10);
    const endIndex = startIndex + (this.pageSize || 10);
    this.pagedData = this.dataSource.slice(startIndex, endIndex);
  }

  // ─── Column Collapse / Expand ──────────────────────────────────────────────

  toggleColumnCollapse(column: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.collapsedColumns.has(column)) {
      this.collapsedColumns.delete(column);
      this.columnWidths[column] = this.preCollapseWidths[column] || this.DEFAULT_COL_WIDTH;
    } else {
      this.preCollapseWidths[column] = this.columnWidths[column] || this.DEFAULT_COL_WIDTH;
      this.collapsedColumns.add(column);
      this.columnWidths[column] = this.COLLAPSED_COL_WIDTH;
    }
  }

  isColumnCollapsed(column: string): boolean {
    return this.collapsedColumns.has(column);
  }

  filterByLegend(legendType: string | null): void {
    console.log('Clicked legendType:', legendType);
    console.log('Active legend before click:', this.activeLegend);

    if (this.activeLegend === legendType) {
      console.log('Same legend clicked again. Resetting filter.');
      this.activeLegend = null;
      this.dataSource = [...this.originalDataSource];
    } else {
      this.activeLegend = legendType;
      if (legendType) {
        console.log('Filtering data for legendType:', legendType);
        const filteredData = this.originalDataSource.filter(
          (row) =>
            (row.option_impact_drivers || '').trim().toLowerCase() ===
            (legendType || '').trim().toLowerCase(),
        );

        console.log('Filtered data:', filteredData);
        this.dataSource = filteredData;
      } else {
        console.log('LegendType is null, resetting dataSource to original.');
        this.dataSource = [...this.originalDataSource];
      }
    }

    console.log('DataSource after filtering:', this.dataSource);
    this.pageIndex = 0;
    this.setPagedData();
    this.paginatorComp?.updatePagination(this.dataSource.length, this.pageIndex);
  }

  /**
   * Determine if a vertical partition line should be shown between this column
   * and the next one. Per UI Standards (Point 5):
   *
   *   data.showColumnDividers = true                → show on ALL columns
   *   data.showColumnDividers = false (default)     → no dividers
   *   data.dividerColumns = ['col1', 'col3']        → show only after specified columns
   *
   * If `dividerColumns` is provided, it takes precedence over `showColumnDividers`.
   */
  showDivider(column: string): boolean {
    const selective = this.data?.dividerColumns;
    if (Array.isArray(selective) && selective.length > 0) {
      return selective.includes(column);
    }
    return this.data?.showColumnDividers === true;
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
